import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";

import { useAuth } from "../../auth/AuthContext";
import AdminRunEvidence from "../../components/AdminRunEvidence";
import { Button, ConfirmDialog, TextArea } from "../../components/ui";
import { hideChatAndFastTrack } from "../../lib/featureFlags";
import {
  Empty,
  ErrorState,
  InlineNotice,
  Loading,
  Panel,
} from "../../components/WorkspacePrimitives";
import {
  useAgentRun,
  useAgentConfiguration,
  useCancelAgentRun,
  useDeleteAgentRun,
  useUpdateAgentRunDraft,
} from "../api/e2e";
import AgentStatusPill from "./AgentStatusPill";
import { duration, shortStamp } from "../lib/format";
import AgentAttributionLabel from "./AgentAttribution";
import AgentVisibilityControl from "./AgentVisibilityControl";

export default function AgentRunDetails({
  runId,
  companyId,
  onClose,
  showVisibilityControl = true,
}: {
  runId: string;
  companyId?: string;
  onClose?: () => void;
  showVisibilityControl?: boolean;
}) {
  const auth = useAuth();
  const navigate = useNavigate();
  const restrictedProductionUi = hideChatAndFastTrack();
  const query = useAgentRun(runId);
  const configuration = useAgentConfiguration();
  const cancel = useCancelAgentRun();
  const deleteRun = useDeleteAgentRun();
  const updateDraft = useUpdateAgentRunDraft();
  const [draftHypothesis, setDraftHypothesis] = useState("");
  const [draftIterationMessage, setDraftIterationMessage] = useState("");
  const [confirmation, setConfirmation] = useState<"cancel" | "delete" | null>(
    null,
  );

  useEffect(() => {
    if (query.data?.state.kind === "draft") {
      setDraftHypothesis(query.data.hypothesis);
      setDraftIterationMessage(query.data.iteration_message ?? "");
    }
  }, [
    query.data?.hypothesis,
    query.data?.iteration_message,
    query.data?.state.kind,
  ]);

  if (query.isPending && !query.data) {
    return <Loading label="Loading run details…" />;
  }
  if (query.error && !query.data) {
    return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  }
  const run = query.data;
  if (!run) return null;
  if (companyId && run.company_id !== companyId) {
    return <Empty>This run is not available for this company.</Empty>;
  }

  const owned =
    !auth.configured ||
    (auth.identity !== null &&
      run.started_by?.subject === auth.identity.subject);
  const cancellable =
    run.state.kind !== "terminal" && (auth.canOperate || owned);
  const deletable = auth.canOperate || owned;
  const deleteLabel =
    run.state.kind === "draft" ? "Delete draft" : "Delete run";
  const iterationBlocked =
    run.base_model !== null && configuration.data?.iteration_enabled !== true;
  const failure =
    run.state.kind === "terminal" && run.state.outcome === "failed"
      ? run.state.failure
      : null;

  const cancelRun = () => {
    cancel.mutate(run.id, {
      onSuccess: () => {
        toast.success("Cancellation requested.");
        setConfirmation(null);
      },
      onError: (error) => toast.error(error.message),
    });
  };

  const saveDraft = () => {
    updateDraft.mutate(
      {
        runId: run.id,
        hypothesis: draftHypothesis,
        ...(run.base_model ? { iterationMessage: draftIterationMessage } : {}),
      },
      {
        onSuccess: () => toast.success("Draft run updated."),
        onError: (error) => toast.error(error.message),
      },
    );
  };

  const removeRun = () => {
    deleteRun.mutate(run.id, {
      onSuccess: () => {
        toast.success("Run deleted.");
        setConfirmation(null);
        if (onClose) onClose();
        else navigate(`/companies/${run.company_id}/runs`, { replace: true });
      },
      onError: (error) => toast.error(error.message),
    });
  };

  return (
    <section className="company-run-detail" aria-label={`Run ${run.id}`}>
      <header className="company-run-detail__header">
        <div>
          <div className="eyebrow">Producing run</div>
          <h2>Run {run.id.slice(0, 8)}</h2>
          <div className="title-status">
            <AgentStatusPill state={run.state} />
            <span>
              {run.parameters.input_mode === "canalyst"
                ? "Canalyst workbook"
                : "Public research"}
            </span>
            <span aria-label="Run tier">
              Tier: {run.parameters.fast ? "Fast" : "Standard"}
            </span>
          </div>
        </div>
        <div className="company-run-detail__actions">
          {showVisibilityControl && (
            <AgentVisibilityControl
              runId={run.id}
              visibility={run.visibility}
              ownerSubject={run.started_by?.subject ?? null}
            />
          )}
          {deletable && (
            <Button
              tone="danger"
              disabled={deleteRun.isPending}
              onClick={() => setConfirmation("delete")}
            >
              {deleteRun.isPending ? "Deleting…" : deleteLabel}
            </Button>
          )}
          {cancellable && run.state.kind !== "draft" ? (
            <Button
              tone="danger"
              disabled={cancel.isPending}
              onClick={() => setConfirmation("cancel")}
            >
              {cancel.isPending ? "Canceling…" : "Cancel run"}
            </Button>
          ) : null}
          {onClose && (
            <Button tone="quiet" onClick={onClose}>
              Close details
            </Button>
          )}
        </div>
      </header>

      {query.error && (
        <InlineNotice tone="warning">
          Live updates are paused. Showing the latest durable state.
        </InlineNotice>
      )}
      {failure && (
        <section className="failure-callout" role="alert">
          <div className="eyebrow">Run failed · {failure.category}</div>
          <h2>{failure.safe_message}</h2>
          <p>
            Lifecycle phase {failure.phase} · code{" "}
            <span className="mono">{failure.code}</span>
            {failure.retryable ? " · a fresh run may be retried" : ""}
          </p>
        </section>
      )}

      <div className="build-detail-grid">
        <Panel title="Run timeline">
          <ol className="timeline">
            <li className="is-complete">
              <span className="timeline__marker" aria-hidden="true" />
              <span className="timeline__label">Queued</span>
              <time dateTime={run.created_at}>
                {shortStamp(run.created_at)}
              </time>
            </li>
            <li className={run.started_at ? "is-complete" : "is-pending"}>
              <span className="timeline__marker" aria-hidden="true" />
              <span className="timeline__label">Worker started</span>
              <time dateTime={run.started_at ?? undefined}>
                {shortStamp(run.started_at)}
              </time>
            </li>
            <li className={run.finished_at ? "is-complete" : "is-pending"}>
              <span className="timeline__marker" aria-hidden="true" />
              <span className="timeline__label">Terminal</span>
              <time dateTime={run.finished_at ?? undefined}>
                {shortStamp(run.finished_at)}
              </time>
            </li>
            <li className="timeline__duration">
              <span>Elapsed</span>
              <strong>{duration(run.started_at, run.finished_at)}</strong>
            </li>
          </ol>
        </Panel>
        <Panel title={auth.canOperate ? "Parameters" : "Run starter"}>
          <dl className="definition-list">
            <div>
              <dt>Started by</dt>
              <dd>
                <AgentAttributionLabel
                  attribution={run.started_by}
                  empty={run.state.kind === "draft" ? "Not started" : "—"}
                />
              </dd>
            </div>
            <div>
              <dt>Starting model</dt>
              <dd>
                {run.base_model ? (
                  <Link
                    to={`/companies/${encodeURIComponent(run.company_id)}/models/${encodeURIComponent(run.base_model.model_version_id)}`}
                  >
                    Version {run.base_model.sequence}
                  </Link>
                ) : (
                  "Fresh"
                )}
              </dd>
            </div>
            {auth.canOperate && (
              <>
                <div>
                  <dt>Agent version</dt>
                  <dd
                    title={
                      run.agent_version?.source_commit_message || undefined
                    }
                  >
                    {run.agent_version
                      ? `${run.agent_version.version} · ${run.agent_version.source_commit_sha.slice(0, 8)}${
                          run.agent_version.source_pull_request_number
                            ? ` · PR #${run.agent_version.source_pull_request_number}`
                            : ""
                        }`
                      : "Legacy"}
                  </dd>
                </div>
                <div>
                  <dt>Model</dt>
                  <dd>{run.parameters.model}</dd>
                </div>
                <div>
                  <dt>Reasoning effort</dt>
                  <dd>{run.parameters.reasoning_effort ?? "—"}</dd>
                </div>
                {!restrictedProductionUi && (
                  <div>
                    <dt>Service tier</dt>
                    <dd>{run.parameters.fast ? "Fast" : "Standard"}</dd>
                  </div>
                )}
                <div>
                  <dt>Timeout</dt>
                  <dd>
                    {Math.round(run.parameters.timeout_seconds / 60)} minutes
                  </dd>
                </div>
                <div>
                  <dt>Workflow</dt>
                  <dd className="mono">{run.workflow_id}</dd>
                </div>
                <div>
                  <dt>State version</dt>
                  <dd>{run.state_version}</dd>
                </div>
              </>
            )}
          </dl>
        </Panel>
      </div>

      {run.state.kind === "draft" && owned ? (
        <Panel
          title={
            run.base_model ? "Iteration instructions" : "Modeling hypothesis"
          }
          className="agent-hypotheses-panel"
        >
          <div className="agent-draft-editor">
            {iterationBlocked && (
              <InlineNotice tone="warning">
                Iteration is unavailable. Create a fresh run instead of editing
                or starting this draft.
              </InlineNotice>
            )}
            <TextArea
              id={`run-${run.id}-hypothesis`}
              label={
                run.base_model
                  ? "Modeling hypothesis · required"
                  : "Hypothesis · optional"
              }
              value={draftHypothesis}
              rows={3}
              maxLength={2000}
              disabled={updateDraft.isPending || iterationBlocked}
              onValueChange={setDraftHypothesis}
            />
            {run.base_model && (
              <TextArea
                id={`run-${run.id}-iteration-message`}
                label="Iteration message · required"
                value={draftIterationMessage}
                rows={3}
                maxLength={2000}
                disabled={updateDraft.isPending || iterationBlocked}
                onValueChange={setDraftIterationMessage}
              />
            )}
            <div className="agent-draft-editor__actions">
              <Button
                disabled={
                  iterationBlocked ||
                  updateDraft.isPending ||
                  (draftHypothesis.trim() === run.hypothesis &&
                    draftIterationMessage.trim() ===
                      (run.iteration_message ?? "")) ||
                  (run.base_model !== null &&
                    (draftHypothesis.trim() === "" ||
                      draftIterationMessage.trim() === ""))
                }
                onClick={saveDraft}
              >
                {updateDraft.isPending ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </div>
        </Panel>
      ) : (
        <>
          {run.hypothesis ? (
            <Panel
              title="Modeling hypothesis"
              className="agent-hypotheses-panel"
            >
              <p>{run.hypothesis}</p>
            </Panel>
          ) : null}
          {run.iteration_message ? (
            <Panel title="Iteration message" className="agent-hypotheses-panel">
              <p>{run.iteration_message}</p>
            </Panel>
          ) : null}
        </>
      )}

      <AdminRunEvidence runId={run.id} />

      {confirmation && (
        <ConfirmDialog
          title={
            confirmation === "delete"
              ? `Delete ${run.ticker} ${run.state.kind === "draft" ? "draft" : "run"}?`
              : `Cancel ${run.ticker} run?`
          }
          description={
            confirmation === "delete"
              ? "This removes the run and its published model from the workspace and stops active execution. Company documents and audit records are retained."
              : "The active model process will stop and this run will remain in history as canceled."
          }
          confirmLabel={confirmation === "delete" ? deleteLabel : "Cancel run"}
          busy={deleteRun.isPending || cancel.isPending}
          onCancel={() => setConfirmation(null)}
          onConfirm={confirmation === "delete" ? removeRun : cancelRun}
        />
      )}
    </section>
  );
}
